import { http } from './request';

/** 用户类型：超管 / 白名单管理员 / 咨询顾问 / 注册用户 */
export type UserType = 'super' | 'admin' | 'consultant' | 'user';

/** 完整用户资料（含可选扩展字段） */
export interface UserProfile {
  phone?: string;
  unit?: string;
  title?: string;
  bizType?: string;
  nickname?: string;
  idCardNo?: string;
  orgCode?: string;
  address?: string;
  email?: string;
  wechat?: string;
  landline?: string;
  industry?: string;
  concernedAssets?: string[];
  concernedRegions?: string[];
  termsVersion?: string | null;
  loginAt?: string | null;
  lastSeenAt?: string | null;
  regionCode?: string | null;
  regionName?: string | null;
}

export interface SendCodeResp {
  ok: true;
  expireIn: number;
  provider: 'mock' | 'tencent' | 'disabled';
  /** 仅 mock / disabled driver 返回，便于开发调试 */
  debugCode?: string;
  /** 命中 60 秒限频：本次未重发，验证码此前已送达 */
  alreadySent?: boolean;
  /** 距下次可重发的秒数（alreadySent 时有值） */
  retryAfter?: number;
  /** 服务端提示文案（alreadySent 时有值） */
  message?: string;
}

export interface LoginResp {
  ok: true;
  needRegister?: boolean;
  phoneMasked: string;
  isAdmin?: boolean;
  termsVersion?: string | null;
  /** needRegister 时：手机号已验证后填写登记资料的有效期秒数 */
  registerGraceSec?: number;
}

export interface RegisterResp {
  ok: true;
  phoneMasked: string;
  isAdmin: boolean;
  /** 用户类型 */
  userType?: UserType;
  /** 机会帐区域额度（区县额度数；-1 = 不限） */
  regionQuota?: number;
  /** 实际授予的区域 */
  grantedRegions?: string[];
  /** 额度占用明细 */
  quotaUsage?: QuotaUsage;
  /** 区域配置是否已锁定 */
  locked?: boolean;
  /** 服务端裁剪了区域（超出额度） */
  trimmed?: boolean;
  message?: string;
}

/** 配额占用明细（独立双配额：区县额度与市级名额互不占用） */
export interface QuotaUsage {
  /** 已解锁的市级数量 */
  cityCount: number;
  /** 市级名额上限 */
  cityCapacity: number;
  /** 剩余市级名额 */
  remainingCities: number;
  /** 已解锁的区县级数量 */
  countyCount: number;
  /** 区县额度上限 */
  countyCapacity: number;
  /** 剩余区县额度 */
  remainingCounties: number;
  /** 剩余区县额度（右上角空闲额度提示口径） */
  remainingQuota: number;
}

export interface MeResp {
  loggedIn: boolean;
  phoneMasked: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** 用户类型 */
  userType: UserType;
  /** 机会帐区域额度（区县额度数；-1 = 不限） */
  regionQuota: number;
  /** 已授予完整权限的区域 */
  grantedRegions: string[];
  /** 是否拥有全部区域（超管 / 管理员 / 顾问） */
  hasAllRegions?: boolean;
  /** 额度占用明细 */
  quotaUsage?: QuotaUsage;
  /** 区域配置是否已锁定（提交后不可再改） */
  locked?: boolean;
  /** 省级机会帐是否可看（所有注册用户均为 true） */
  provinceAccess?: boolean;
  /** 从未配置过任何区域 → 登录后需引导配置 */
  needsRegionSetup?: boolean;
  profile: UserProfile | null;
}

/** 额度与区域配置查询结果 */
export interface EntitlementResp {
  userType: UserType;
  quota: number;
  grantedRegions: string[];
  quotaUsage: QuotaUsage;
  /** 是否已锁定（提交后不可自行变更） */
  locked: boolean;
  /** 是否可继续配置（未锁定，或额度尚有余额） */
  canConfigure: boolean;
  /** 换算比例（后台规则，前端不展示具体换算说明） */
  countiesPerCity: number;
  province: string;
}

/** 区域访问权限查询结果 */
export interface RegionAccessResp {
  allowed: boolean;
  reason: 'not-logged-in' | 'quota-exceeded' | 'granted' | 'all';
  userType: UserType;
  quota: number;
  grantedRegions?: string[];
}

export const authApi = {
  /** 用户须知与免责声明 */
  notice: (v = 'v1') => http.get<{ version: string; title: string; summary: string; sections: any[] }>(`/auth/notice?v=${v}`),

  /** 发送验证码 */
  sendCode: (phone: string, deviceId: string) =>
    http.post<SendCodeResp>('/auth/send-code', { phone, deviceId }),

  /** 登录：已注册 → 直接签发；未注册 → 返回 needRegister=true */
  login: (phone: string, code: string) =>
    http.post<LoginResp>('/auth/login', { phone, code }),

  /** 注册：完整资料写入 lead + 签发会话 */
  register: (body: {
    phone: string;
    code: string;
    unit: string;
    title: string;
    bizType: string;
    nickname?: string;
    idCardNo?: string;
    orgCode?: string;
    address?: string;
    email?: string;
    wechat?: string;
    landline?: string;
    industry?: string;
    concernedAssets?: string[];
    concernedRegions?: string[];
    /** 自选的机会帐区域（1 个市 + ≤N 个区县） */
    selectedRegions?: string[];
    agreedTermsVersion?: string;
  }) => http.post<RegisterResp>('/auth/register', body),

  /** 补充资料（PATCH） */
  patchProfile: (body: Partial<UserProfile>) =>
    http.patch<{ ok: true }>('/auth/me/profile', body),

  /** 当前会话 */
  me: () => http.get<MeResp>('/auth/me'),

  /** 区域访问权限：能否查看该区域的完整机会帐 */
  regionAccess: (region: string) =>
    http.get<RegionAccessResp>(`/auth/region-access?region=${encodeURIComponent(region)}`),

  /** 额度与区域配置查询 */
  entitlement: () => http.get<EntitlementResp>('/auth/entitlement'),

  /** 补选区域（配额未用满时；只增不减，提交后不可再改） */
  saveRegions: (regions: string[]) =>
    http.post<{ ok: true; grantedRegions: string[]; quotaUsage: QuotaUsage; quota: number; trimmed?: boolean; message?: string }>(
      '/auth/regions', { regions }
    ),

  /**
   * 邀请码兑换（**预留**，当前界面不暴露入口）
   * 后续确定邀请码机制后启用：输入正确邀请码 → 增加配额并解锁新区域
   */
  redeemInvite: (code: string) =>
    http.post<{ ok: true; regionQuota?: number; grantedRegions?: string[] }>('/auth/invite/redeem', { code }),

  /** 注销 */
  logout: () => http.post<{ ok: true }>('/auth/logout')
};